/**
 * Guided-planning host for the shared funding-source picker.
 *
 * Supplies the guided pieces the shared screen must not know about: the selection saved in the guided
 * session, guided funding sources (a snapshot for completed sessions), and wizard navigation.
 * Continue saves `selectedAssets` into the session and moves the wizard on; on a finalized session a
 * changed selection reopens it (`useReopenSession`). The screen renders inside `StepLayout`.
 */
import { useWizard } from 'react-use-wizard';

import { FundingSourceSelection } from '@/features/funding-sources';
import {
  useSession,
  useSessionMutations,
  useIsSessionComplete,
} from '@/features/guided-financial-planning/context';
import { useFundingSources } from '@/features/guided-financial-planning/hooks/use-funding-sources';
import { useReopenSession } from '@/features/guided-financial-planning/hooks/use-reopen-session';
import { StepLayout } from '@/features/guided-financial-planning/components/step-layout';

function isSameSelection(a: readonly AssetTypeId[], b: readonly AssetTypeId[]) {
  return a.length === b.length && a.every(id => b.includes(id));
}

export function AssetSelectionStep() {
  const { nextStep } = useWizard();
  const { session, isLoading } = useSession();
  const { updateStepData, isSaving } = useSessionMutations();
  const isSessionComplete = useIsSessionComplete();
  const { reopen, isReopening } = useReopenSession();
  const fundingSources = useFundingSources();
  const savedSelection = session?.stepData?.selectedAssets ?? null;

  return (
    <FundingSourceSelection
      existingSelfFundingSources={fundingSources?.selfFunding}
      savedSelection={savedSelection}
      isLoading={isLoading}
      readOnly={isReopening}
      isSaving={isSaving || isReopening}
      onContinue={async selection => {
        if (!isSessionComplete) {
          await updateStepData({ selectedAssets: selection });
        } else if (!isSameSelection(selection, savedSelection ?? [])) {
          if (!(await reopen({ selectedAssets: selection }))) return;
        }
        nextStep();
      }}
      frame={parts => (
        <StepLayout
          title={parts.title}
          subtitle={parts.subtitle}
          isNextDisabled={parts.primaryAction?.disabled}
          nextButtonLabel={parts.primaryAction?.label}
          onNext={parts.primaryAction?.onSelect}
        >
          {parts.content}
        </StepLayout>
      )}
    />
  );
}
